// Orders history list page at /orders.
import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Trash2, SlidersHorizontal, X, XCircle } from "lucide-react";
import { apiGet, apiDelete, apiPost } from "@/lib/api/client";
import { formatDateTime } from "@/lib/format/date";
import { translateApiError } from "@/lib/errors/error-map";
import { useAuthStore } from "@/lib/auth/store";
import { toast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { CategoryMultiSelect } from "@/features/categories/category-multi-select";

/** Debounce a rapidly-changing value so typing doesn't fire a request per keystroke. */
function useDebounced<T>(value: T, ms = 350): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

interface OrderItemSummary {
  name: string;
  qty: number;
}

interface Order {
  id: string;
  code: string;
  status: string;
  orderType?: string | null;
  subtotal: string;
  discountAmount: string;
  total: string;
  paidAmount: string;
  debtAmount: string;
  paymentMethod?: string | null;
  cashierEmail?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  customerAddress?: string | null;
  note?: string | null;
  createdAt: string;
  paidAt?: string | null;
  /** [{name, qty}] gộp theo sản phẩm (không IMEI) — cột "Sản phẩm". */
  itemsSummary?: OrderItemSummary[];
}

const ORDER_TYPE_LABEL: Record<string, string> = {
  retail: "Bán lẻ",
  wholesale: "Bán sỉ",
};

const STATUS_LABEL: Record<string, string> = {
  draft: "Nháp",
  pending_payment: "Chờ TT",
  paid: "Đã thanh toán",
  partial_paid: "Thanh toán một phần",
  voided: "Đã huỷ",
  cancelled: "Đã hủy bỏ",
};

// Human labels for payment methods. "migrated" marks orders imported from the
// old nhanh.vn system, where the original method is unknown.
const PAYMENT_LABEL: Record<string, string> = {
  cash: "Tiền mặt",
  qr: "QR / SePay",
  bank_manual: "CK tay",
  mixed: "Tiền mặt + CK",
  migrated: "Dữ liệu cũ",
  unknown: "Khác",
};

function fmtPayment(method?: string | null) {
  if (!method) return "—";
  return PAYMENT_LABEL[method] ?? method;
}

function fmtVND(s: string) {
  const n = parseFloat(s);
  if (isNaN(n)) return "0 ₫";
  return new Intl.NumberFormat("vi-VN").format(n) + " ₫";
}

function fmtDate(s: string) {
  return formatDateTime(s);
}

// Giữ bộ lọc khi rời trang (bấm xem chi tiết đơn) rồi quay lại — Sếp yêu cầu
// 20/08: quay lại phải là màn đã lọc, không phải trang trắng. sessionStorage:
// sống theo tab, đóng tab là quên (mở app mới vẫn sạch lọc).
const ORDERS_FILTER_KEY = "orders-list-filters-v1";

interface SavedOrderFilters {
  code: string;
  status: string;
  orderType: string;
  dateFrom: string;
  dateTo: string;
  showAdvanced: boolean;
  customer: string;
  address: string;
  categoryIds: string[];
  product: string;
  imei: string;
  page: number;
}

function loadSavedFilters(): Partial<SavedOrderFilters> {
  try {
    return JSON.parse(sessionStorage.getItem(ORDERS_FILTER_KEY) ?? "{}") as Partial<SavedOrderFilters>;
  } catch {
    return {};
  }
}

export function OrdersListPage() {
  const navigate = useNavigate();
  const saved = loadSavedFilters();
  const [code, setCode] = useState(saved.code ?? "");
  const [status, setStatus] = useState(saved.status ?? "");
  // Mặc định màn Đơn hàng mở TỔNG HỢP cả Lẻ & Sỉ (Sếp đổi lại 14/08; trước đó
  // 28/07 từng để mặc định Bán lẻ) — vẫn lọc riêng được qua dropdown.
  const [orderType, setOrderType] = useState(saved.orderType ?? "");
  const [dateFrom, setDateFrom] = useState(saved.dateFrom ?? "");
  const [dateTo, setDateTo] = useState(saved.dateTo ?? "");
  // Advanced free-text filters (all combinable).
  const [showAdvanced, setShowAdvanced] = useState(saved.showAdvanced ?? false);
  const [customer, setCustomer] = useState(saved.customer ?? "");
  const [address, setAddress] = useState(saved.address ?? "");
  const [categoryIds, setCategoryIds] = useState<string[]>(saved.categoryIds ?? []);
  const [product, setProduct] = useState(saved.product ?? "");
  const [imei, setImei] = useState(saved.imei ?? "");
  const [page, setPage] = useState(saved.page ?? 0);
  const limit = 20;

  // Lưu lại mỗi khi đổi — sessionStorage có thể bị chặn (private mode) nên bọc try.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        ORDERS_FILTER_KEY,
        JSON.stringify({
          code, status, orderType, dateFrom, dateTo, showAdvanced,
          customer, address, categoryIds, product, imei, page,
        } satisfies SavedOrderFilters),
      );
    } catch {
      // bỏ qua — không lưu được thì chỉ mất tiện nghi nhớ lọc
    }
  }, [code, status, orderType, dateFrom, dateTo, showAdvanced, customer, address, categoryIds, product, imei, page]);

  // Debounce free-text inputs so typing doesn't fire a request per keystroke.
  const dCode = useDebounced(code);
  const dCustomer = useDebounced(customer);
  const dAddress = useDebounced(address);
  const dProduct = useDebounced(product);
  const dImei = useDebounced(imei);

  const hasAdvanced = !!(dCustomer || dAddress || categoryIds.length || dProduct || dImei);

  // Reset to first page whenever any filter changes — nhưng BỎ QUA lần chạy
  // đầu sau mount, kẻo xóa mất số trang vừa khôi phục từ sessionStorage.
  const skipFirstPageReset = useRef(true);
  useEffect(() => {
    if (skipFirstPageReset.current) {
      skipFirstPageReset.current = false;
      return;
    }
    setPage(0);
  }, [dCode, status, orderType, dateFrom, dateTo, dCustomer, dAddress, categoryIds, dProduct, dImei]);

  const params = {
    limit,
    offset: page * limit,
    ...(dCode ? { code: dCode } : {}),
    // Mặc định ẩn đơn nháp / chờ thanh toán (chỉ hiện đơn thật). Chọn trạng thái
    // "Nháp" ở bộ lọc thì xem được đơn nháp.
    ...(status ? { status } : { exclude_status: "draft,pending_payment" }),
    ...(orderType ? { order_type: orderType } : {}),
    ...(dateFrom ? { date_from: dateFrom } : {}),
    ...(dateTo ? { date_to: dateTo } : {}),
    ...(dCustomer ? { customer: dCustomer } : {}),
    ...(dAddress ? { address: dAddress } : {}),
    ...(categoryIds.length ? { category_ids: categoryIds.join(",") } : {}),
    ...(dProduct ? { product: dProduct } : {}),
    ...(dImei ? { imei: dImei } : {}),
  };

  function clearAll() {
    setCode("");
    setStatus("");
    setOrderType("");
    setDateFrom("");
    setDateTo("");
    setCustomer("");
    setAddress("");
    setCategoryIds([]);
    setProduct("");
    setImei("");
    setPage(0);
    try {
      sessionStorage.removeItem(ORDERS_FILTER_KEY);
    } catch {
      // ignore
    }
  }

  const { data, isFetching } = useQuery({
    queryKey: ["orders", params],
    queryFn: () => apiGet<Order[]>("/v1/orders", params),
    placeholderData: (prev) => prev,
  });

  const orders = data ?? [];

  // Quản lý phiếu (admin/quản lý/được cấp quyền): xóa đơn nháp + huỷ đơn đã bán.
  const canManage = useAuthStore(
    (s) => s.user?.role === "admin" || s.user?.role === "manager" || !!s.user?.canManageOrders,
  );
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: (id: string) => apiDelete(`/v1/orders/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Đã xóa hóa đơn nháp");
    },
    onError: (e) => toast.error(translateApiError(e)),
  });
  const voidMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiPost(`/v1/orders/${id}/void`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Đã huỷ đơn (hàng đã trả về kho)");
    },
    onError: (e) => toast.error(translateApiError(e)),
  });

  const colCount = canManage ? 11 : 10;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Đơn hàng</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Tìm mã đơn..."
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="w-44"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-md border bg-card px-3 py-2 text-sm"
        >
          <option value="">Tất cả trạng thái</option>
          {Object.entries(STATUS_LABEL).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select
          value={orderType}
          onChange={(e) => setOrderType(e.target.value)}
          className="rounded-md border bg-card px-3 py-2 text-sm"
        >
          <option value="">Lẻ &amp; Sỉ</option>
          <option value="retail">Bán lẻ</option>
          <option value="wholesale">Bán sỉ</option>
        </select>
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="w-40"
        />
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="w-40"
        />
        <Button
          variant={showAdvanced ? "default" : "outline"}
          size="sm"
          onClick={() => setShowAdvanced((v) => !v)}
          className="gap-1.5"
        >
          <SlidersHorizontal size={15} />
          Tìm nâng cao
          {hasAdvanced && <span className="ml-1 rounded-full bg-primary-foreground/20 px-1.5 text-xs">•</span>}
        </Button>
        {(code || status || orderType || dateFrom || dateTo || hasAdvanced) && (
          <Button variant="ghost" size="sm" onClick={clearAll} className="gap-1 text-muted-foreground">
            <X size={14} /> Xóa lọc
          </Button>
        )}
      </div>

      {/* Advanced filters — all type-to-search, combinable */}
      {showAdvanced && (
        <div className="grid grid-cols-1 gap-3 rounded-lg border bg-muted/30 p-4 sm:grid-cols-2 lg:grid-cols-3">
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Khách hàng (tên / SĐT)</span>
            <Input
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              placeholder="Nhập tên hoặc số điện thoại…"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Địa chỉ</span>
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Nhập địa chỉ…"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Nhóm sản phẩm</span>
            <CategoryMultiSelect values={categoryIds} onChange={setCategoryIds} />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Sản phẩm (tên / SKU)</span>
            <Input
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder="Tên sản phẩm hoặc mã SKU…"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">IMEI / Serial</span>
            <Input
              value={imei}
              onChange={(e) => setImei(e.target.value)}
              placeholder="Nhập IMEI…"
              className="font-mono"
            />
          </label>
        </div>
      )}

      <Table>
        <THead>
          <Tr>
            {/* Sản phẩm + Tổng tiền đứng ngay sau Mã đơn — trên mobile cuộn ngang
                thấy hàng bán và tiền trước tiên (Sếp yêu cầu 28/07). */}
            <Th>Mã đơn</Th>
            <Th>Sản phẩm</Th>
            <Th className="text-right">Tổng tiền</Th>
            <Th>Thanh toán</Th>
            <Th>Trạng thái</Th>
            <Th>Khách hàng</Th>
            <Th>Loại</Th>
            <Th>Người tạo</Th>
            <Th className="text-right">CK</Th>
            <Th>Ghi chú</Th>
            {canManage && <Th className="text-right">Thao tác</Th>}
          </Tr>
        </THead>
        <TBody>
          {isFetching && (
            <Tr><Td colSpan={colCount} className="text-center text-muted-foreground">Đang tải...</Td></Tr>
          )}
          {!isFetching && orders.length === 0 && (
            <Tr><Td colSpan={colCount} className="text-center text-muted-foreground">Không có đơn hàng</Td></Tr>
          )}
          {orders.map((o) => (
            <Tr
              key={o.id}
              className="cursor-pointer"
              onClick={() => navigate({ to: `/orders/${o.id}` })}
            >
              <Td className="font-mono font-semibold">{o.code}</Td>
              {/* Sản phẩm: tên + SL gộp theo SP, không IMEI; quá 4 dòng thì gọn lại */}
              <Td className="min-w-[180px] max-w-[280px] text-sm">
                {(o.itemsSummary?.length ?? 0) > 0 ? (
                  <div className="space-y-0.5">
                    {o.itemsSummary!.slice(0, 4).map((it, idx) => (
                      <div key={idx} className="flex justify-between gap-2">
                        <span className="truncate" title={it.name}>{it.name}</span>
                        <span className="shrink-0 font-mono text-xs text-muted-foreground">x{it.qty}</span>
                      </div>
                    ))}
                    {o.itemsSummary!.length > 4 && (
                      <div className="text-xs text-muted-foreground">
                        +{o.itemsSummary!.length - 4} sản phẩm khác
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </Td>
              <Td className="text-right font-semibold text-green-700">{fmtVND(o.total)}</Td>
              <Td className="text-sm">
                <div>{fmtPayment(o.paymentMethod)}</div>
                {parseFloat(o.paidAmount) > 0 && parseFloat(o.debtAmount) > 0 && (
                  <div className="text-xs text-muted-foreground">Đã trả: {fmtVND(o.paidAmount)}</div>
                )}
                {parseFloat(o.debtAmount) > 0 && (
                  <div className="text-xs font-medium text-red-600">Còn nợ: {fmtVND(o.debtAmount)}</div>
                )}
              </Td>
              <Td>
                <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium
                  ${o.status === "paid" ? "bg-green-100 text-green-800" :
                    o.status === "voided" ? "bg-red-100 text-red-800" :
                    "bg-muted text-muted-foreground"}`}>
                  {STATUS_LABEL[o.status] ?? o.status}
                </span>
              </Td>
              <Td className="max-w-[220px] text-sm">
                {o.customerName || o.customerPhone ? (
                  <div className="min-w-0">
                    <div className="whitespace-normal break-words font-medium">{o.customerName || "—"}</div>
                    {o.customerPhone && (
                      <div className="font-mono text-xs text-muted-foreground">{o.customerPhone}</div>
                    )}
                    {o.customerAddress && (
                      <div className="truncate text-xs text-muted-foreground" title={o.customerAddress}>
                        {o.customerAddress}
                      </div>
                    )}
                  </div>
                ) : "—"}
              </Td>
              <Td>
                {o.orderType ? (
                  <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
                    o.orderType === "wholesale"
                      ? "bg-purple-100 text-purple-800"
                      : "bg-blue-100 text-blue-800"
                  }`}>
                    {ORDER_TYPE_LABEL[o.orderType] ?? o.orderType}
                  </span>
                ) : "—"}
              </Td>
              <Td className="text-sm">
                <div>{o.cashierEmail ?? "—"}</div>
                <div className="text-xs text-muted-foreground">{fmtDate(o.createdAt)}</div>
              </Td>
              <Td className="text-right text-sm">
                {parseFloat(o.discountAmount) > 0 ? (
                  <div>
                    <div className="text-amber-600">{fmtVND(o.discountAmount)}</div>
                    {parseFloat(o.subtotal) > 0 && (
                      <div className="text-xs text-muted-foreground">
                        ({((parseFloat(o.discountAmount) / parseFloat(o.subtotal)) * 100).toFixed(1)}%)
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </Td>
              <Td className="max-w-[140px] truncate text-sm text-muted-foreground" title={o.note ?? ""}>
                {o.note || "—"}
              </Td>
              {canManage && (
                <Td className="text-right">
                  {o.status === "draft" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      disabled={del.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Xóa hóa đơn nháp ${o.code}?`)) del.mutate(o.id);
                      }}
                      aria-label={`Xóa hóa đơn nháp ${o.code}`}
                      title="Xóa đơn nháp"
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                  {(o.status === "paid" || o.status === "partial_paid") && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      disabled={voidMut.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        const reason = window.prompt(`Huỷ đơn ${o.code}? Hàng sẽ trả về kho.\nNhập lý do huỷ:`);
                        if (reason && reason.trim()) voidMut.mutate({ id: o.id, reason: reason.trim() });
                      }}
                      aria-label={`Huỷ đơn ${o.code}`}
                      title="Huỷ đơn (trả hàng về kho)"
                    >
                      <XCircle size={16} />
                    </Button>
                  )}
                </Td>
              )}
            </Tr>
          ))}
        </TBody>
      </Table>

      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
          Trước
        </Button>
        <span className="text-sm text-muted-foreground">Trang {page + 1}</span>
        <Button variant="outline" size="sm" disabled={orders.length < limit} onClick={() => setPage((p) => p + 1)}>
          Tiếp
        </Button>
      </div>
    </div>
  );
}
